import { useState } from 'react'

import {
  ActionIcon,
  Box,
  Button as MantineButton,
  Menu,
  Popover,
} from '@mantine/core'

import EmployeeAvatarGroupForJobListing from 'src/components/Core/EmployeeAvatarGroupForJobListing'
import IconAdd from 'src/icons/IconAdd'
import IconChevronDown from 'src/icons/IconChevronDown'
import IconClose from 'src/icons/IconClose'

type EmployeeOption = {
  label: string
  value: string
  status?: string | null
}

type JobListingEmployeesAssignmentSectionWithPopUpMenuProps = {
  leadEmployees: EmployeeOption[]
  memberEmployees: EmployeeOption[]
  employeeOptions: EmployeeOption[]
  isEditable: boolean
  onLeadEmployeesChange: (nextLeadEmployeeIds: string[]) => void
  onMemberEmployeesChange: (nextMemberEmployeeIds: string[]) => void
}

const JobListingEmployeesAssignmentSectionWithPopUpMenu = ({
  leadEmployees,
  memberEmployees,
  employeeOptions,
  isEditable,
  onLeadEmployeesChange,
  onMemberEmployeesChange,
}: JobListingEmployeesAssignmentSectionWithPopUpMenuProps) => {
  const [isEmployeesDropdownOpen, setIsEmployeesDropdownOpen] = useState(false)
  const leadEmployeeIds = leadEmployees.map((employee) => employee.value)
  const memberEmployeeIds = memberEmployees.map((employee) => employee.value)
  const availableLeadEmployeeOptions = employeeOptions.filter(
    (employee) =>
      !leadEmployeeIds.includes(employee.value) &&
      !memberEmployeeIds.includes(employee.value)
  )
  const availableMemberEmployeeOptions = employeeOptions.filter(
    (employee) =>
      !memberEmployeeIds.includes(employee.value) &&
      !leadEmployeeIds.includes(employee.value)
  )

  const handleLeadEmployeeAdd = (employeeId: string) => {
    const nextLeadEmployeeIds = [...leadEmployeeIds, employeeId]
    onLeadEmployeesChange(nextLeadEmployeeIds)
    if (memberEmployeeIds.includes(employeeId)) {
      onMemberEmployeesChange(
        memberEmployeeIds.filter((id) => id !== employeeId)
      )
    }
  }

  const handleMemberEmployeeAdd = (employeeId: string) => {
    // Don't add if already a lead - leads take precedence
    if (leadEmployeeIds.includes(employeeId)) {
      return
    }
    const nextMemberEmployeeIds = [...memberEmployeeIds, employeeId]
    onMemberEmployeesChange(nextMemberEmployeeIds)
  }

  return (
    <div className="flex w-full flex-col gap-2 text-[13px]">
      <div className="flex w-full items-center justify-between">
        <div className="text-xs font-medium text-doubleNickel-gray-500">
          Employees
        </div>
        <Popover
          opened={isEditable && isEmployeesDropdownOpen}
          onChange={setIsEmployeesDropdownOpen}
          position="bottom-end"
          shadow="md"
          width={240}
        >
          <Popover.Target>
            <Box
              className={`flex items-center gap-1 rounded px-1 py-0.5 ${isEditable ? 'cursor-pointer hover:bg-doubleNickel-gray-50' : ''
                }`}
              onClick={() => {
                if (!isEditable) {
                  return
                }
                setIsEmployeesDropdownOpen(!isEmployeesDropdownOpen)
              }}
            >
              <EmployeeAvatarGroupForJobListing
                leadEmployees={leadEmployees}
                memberEmployees={memberEmployees}
              />
              {isEditable && (
                <IconChevronDown className="ml-1 h-4 w-4 fill-doubleNickel-white stroke-doubleNickel-gray-700" />
              )}
            </Box>
          </Popover.Target>
          <Popover.Dropdown className="p-2">
            <div className="flex w-full flex-col gap-2">
              <div className="flex w-full flex-col gap-1">
                <div className="text-[11px] font-medium text-doubleNickel-gray-500">
                  Lead Employees ({leadEmployees.length})
                </div>
                <div className="flex w-full flex-col gap-1.5">
                  {leadEmployees.length === 0 && (
                    <div className="rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5 text-xs text-doubleNickel-gray-500">
                      No lead employees
                    </div>
                  )}
                  {leadEmployees.map((employee) => (
                    <div
                      key={employee.value}
                      className="flex items-center justify-between rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5"
                    >
                      <span className="text-xs text-doubleNickel-gray-900">
                        {employee.label}
                      </span>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size={20}
                        onClick={() =>
                          onLeadEmployeesChange(
                            leadEmployeeIds.filter(
                              (employeeId) => employeeId !== employee.value
                            )
                          )
                        }
                      >
                        <IconClose className="h-3.5 fill-none stroke-doubleNickel-gray-500" />
                      </ActionIcon>
                    </div>
                  ))}
                  <Menu
                    shadow="md"
                    position="bottom-start"
                    width="target"
                    withinPortal={false}
                  >
                    <Menu.Target>
                      <MantineButton
                        variant="subtle"
                        color="gray"
                        size="xs"
                        className="w-full justify-center border border-dashed border-doubleNickel-gray-300 text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-50"
                        leftSection={
                          <IconAdd className="h-3 w-3 stroke-doubleNickel-gray-500" />
                        }
                      >
                        Add Lead
                      </MantineButton>
                    </Menu.Target>
                    <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                      {availableLeadEmployeeOptions.length === 0 ? (
                        <Menu.Item disabled>No employees available</Menu.Item>
                      ) : (
                        availableLeadEmployeeOptions.map((employee) => (
                          <Menu.Item
                            key={employee.value}
                            onClick={() => handleLeadEmployeeAdd(employee.value)}
                          >
                            {employee.label}
                          </Menu.Item>
                        ))
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </div>
              </div>
              <div className="flex w-full flex-col gap-1">
                <div className="text-[11px] font-medium text-doubleNickel-gray-500">
                  Members ({memberEmployees.length})
                </div>
                <div className="flex w-full flex-col gap-1.5">
                  {memberEmployees.map((member) => (
                    <div
                      key={member.value}
                      className="flex items-center justify-between rounded border border-doubleNickel-gray-200 bg-white px-2 py-1.5"
                    >
                      <span className="text-xs text-doubleNickel-gray-900">
                        {member.label}
                      </span>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size={20}
                        onClick={() =>
                          onMemberEmployeesChange(
                            memberEmployeeIds.filter(
                              (employeeId) => employeeId !== member.value
                            )
                          )
                        }
                      >
                        <IconClose className="h-3.5 fill-none stroke-doubleNickel-gray-500" />
                      </ActionIcon>
                    </div>
                  ))}
                  <Menu
                    shadow="md"
                    position="bottom-start"
                    width="target"
                    withinPortal={false}
                  >
                    <Menu.Target>
                      <MantineButton
                        variant="subtle"
                        color="gray"
                        size="xs"
                        className="w-full justify-center border border-dashed border-doubleNickel-gray-300 text-doubleNickel-gray-500 hover:bg-doubleNickel-gray-50"
                        leftSection={
                          <IconAdd className="h-3 w-3 stroke-doubleNickel-gray-500" />
                        }
                      >
                        Add Member
                      </MantineButton>
                    </Menu.Target>
                    <Menu.Dropdown className="max-h-[300px] overflow-y-auto">
                      {availableMemberEmployeeOptions.length === 0 ? (
                        <Menu.Item disabled>No employees available</Menu.Item>
                      ) : (
                        availableMemberEmployeeOptions.map((employee) => (
                          <Menu.Item
                            key={employee.value}
                            onClick={() =>
                              handleMemberEmployeeAdd(employee.value)
                            }
                          >
                            {employee.label}
                          </Menu.Item>
                        ))
                      )}
                    </Menu.Dropdown>
                  </Menu>
                </div>
              </div>
            </div>
          </Popover.Dropdown>
        </Popover>
      </div>
    </div>
  )
}

export default JobListingEmployeesAssignmentSectionWithPopUpMenu
